import React, {useEffect, useState} from 'react';
import {IconButton, ListItemButton, Box } from '@mui/material';
import { Checkbox, Grid2, Typography, List, ListItemText, ListItemIcon, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Divider, ListItem, Card, CardContent } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Badge from '@mui/material/Badge';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { DayCalendarSkeleton } from '@mui/x-date-pickers/DayCalendarSkeleton';
import { supabase } from '../components/supabaseClient.tsx';
import NotesIcon from '@mui/icons-material/Notes';
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CakeIcon from "@mui/icons-material/Cake";
import AutoDeleteIcon from "@mui/icons-material/AutoDelete";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";

const initialValue = dayjs();

interface Kalenderinterface {
    id: number;
    created_at: string;
    Titel: string;
    Description: string;
    Date: string;
    Time: string;
    User: string;
    Category: number
}

interface Notiz {
    id: number;
    created_at: string;
    Titel: string;
    Description: string;
    change_date: string;
    User: string;
    is_pinned: boolean;
}

interface TodoProps {
    id: number;
    created_at: string;
    Titel: string;
    Description: string;
    Tasks: string[];
    State: boolean[];
    User: string;
    is_pinned:boolean;
}

interface ShoppingList {
    id: number;
    created_at: string;
    Title: string;
    Description: string;
    Parts: string[];
    Store: string;
    User: string;
    Amount: string[];
    State: boolean[];
    is_pinned: boolean;
}

interface WeatherData {
    temperature: number;
    weathercode: number;
}

const weatherCodeDescriptions: { [key: number]: string } = {
    0: 'Clear sky',
    1: 'Mainly clear, partly cloudy, and overcast',
    2: 'Mainly clear, partly cloudy, and overcast',
    3: 'Mainly clear, partly cloudy, and overcast',
    45: 'Fog and depositing rime fog',
    48: 'Fog and depositing rime fog',
    51: 'Drizzle: Light, moderate, and dense intensity',
    53: 'Drizzle: Light, moderate, and dense intensity',
    55: 'Drizzle: Light, moderate, and dense intensity',
    56: 'Freezing Drizzle: Light and dense intensity',
    57: 'Freezing Drizzle: Light and dense intensity',
    61: 'Rain: Slight, moderate and heavy intensity',
    63: 'Rain: Slight, moderate and heavy intensity',
    65: 'Rain: Slight, moderate and heavy intensity',
    66: 'Freezing Rain: Light and heavy intensity',
    67: 'Freezing Rain: Light and heavy intensity',
    71: 'Snow fall: Slight, moderate, and heavy intensity',
    73: 'Snow fall: Slight, moderate, and heavy intensity',
    75: 'Snow fall: Slight, moderate, and heavy intensity',
    77: 'Snow grains',
    80: 'Rain showers: Slight, moderate, and violent',
    81: 'Rain showers: Slight, moderate, and violent',
    82: 'Rain showers: Slight, moderate, and violent',
    85: 'Snow showers slight and heavy',
    86: 'Snow showers slight and heavy',
    95: 'Thunderstorm: Slight or moderate',
    96: 'Thunderstorm with slight and heavy hail',
    99: 'Thunderstorm with slight and heavy hail',
};

function getCategoryIcon(category: number) {
    switch (category) {
        case 1:
            return <CalendarMonthIcon />;
        case 2:
            return <CakeIcon />;
        case 3:
            return <AutoDeleteIcon />;
        case 4:
            return <MedicalServicesIcon />;
        default:
            return <InsertInvitationIcon />;
    }
}

const getWeatherDescription = (code: number): string => {
    return weatherCodeDescriptions[code] || 'Unknown weather code';
};

function ServerDay(props: PickersDayProps<Dayjs> & { highlightedDays?: number[], onClick: (event: React.MouseEvent<HTMLButtonElement>, day: Dayjs) => void }) {
    const { highlightedDays = [], day, outsideCurrentMonth, onClick, ...other } = props;

    const isSelected =
        !props.outsideCurrentMonth && highlightedDays.indexOf(props.day.date()) >= 0;

    return (
        <Badge
            key={props.day.toString()}
            overlap="circular"
            badgeContent={isSelected ? '🚩' : undefined}
        >
            <PickersDay
                {...other}
                outsideCurrentMonth={outsideCurrentMonth}
                day={day}
                onClick={(event) => onClick(event, day)}
            />
        </Badge>
    );
}

function Dashboard() {
    const [isLoading, setIsLoading] = React.useState(false);
    const [highlightedDays, setHighlightedDays] = React.useState([]);
    const [selectedDate, setSelectedDate] = React.useState<Dayjs | null>(null);
    const [isDialogOpen, setIsDialogOpen] = React.useState(false);
    const [kalender, setKalender] = React.useState<Kalenderinterface[]>([]);
    let kalenderforhighliteddays: Kalenderinterface[] = [];
    let requestAbortController: { current: AbortController | null } = { current: null };
    let currentMonth: number = dayjs().month() + 1;
    let currentYear: number = dayjs().year();
    const [pinnedNotes, setPinnedNotes] = React.useState<Notiz[]>([]);
    const [pinnedTodos, setPinnedTodos] = React.useState<TodoProps[]>([]);
    const [pinnedShoppingLists, setPinnedShoppingLists] = React.useState<ShoppingList[]>([]);
    const [selectedNotiz, setSelectedNotiz] = React.useState<Notiz | null>(null);
    const [isNotizDialogOpen, setIsNotizDialogOpen] = React.useState(false);
    const [weatherData, setWeatherData] = React.useState<WeatherData | null>(null);
    const [selectedTodoItem, setSelectedTodoItem] = useState<TodoProps | null>(null);
    const [isTodoDialogOpen, setIsTodoDialogOpen] = useState(false);
    const [selectedShoppingListItem, setSelectedShoppingListItem] = useState<ShoppingList | null>(null);
    const [isShoppingListDialogOpen, setIsShoppingListDialogOpen] = useState(false);
    let latitude= 0;
    let longitude= 0;
    const [isShoppingListChanged, setIsShoppingListChanged] = useState(false);
    const [isTodoChanged, setIsTodoChanged] = useState(false);

    const getLastDayOfMonth = (year: number, month: number) => {
        return new Date(year, month, 0).getDate();
    };

    function handleListItemClick(event: React.MouseEvent<HTMLDivElement, MouseEvent>, item: Notiz | TodoProps | ShoppingList) {
        if ('Tasks' in item && 'State' in item) {
            // Handle TodoProps
            setSelectedTodoItem(item);
            setIsTodoDialogOpen(true);
        } else if ('Parts' in item && 'Store' in item) {
            // Handle ShoppingList
            setSelectedShoppingListItem(item);
            setIsShoppingListDialogOpen(true);
        } else {
            // Handle Notiz
            setSelectedNotiz(item);
            setIsNotizDialogOpen(true);
        }
    }

    const loadPinnedNotes = async () => {
        const { data } = await supabase
            .from('Notizen')
            .select('*')
            .eq('is_pinned', true);

        setPinnedNotes(data || []);
    };

    const loadPinnedTodos = async () => {
        const { data } = await supabase
            .from('Todo')
            .select('*')
            .eq('is_pinned', true);

        setPinnedTodos(data || []);
    };

    const loadPinnedShoppingLists = async () => {
        const { data } = await supabase
            .from('Shoppinglist')
            .select('*')
            .eq('is_pinned', true);

        setPinnedShoppingLists(data || []);
    };


    const loadKalenderByMonth = async (year: number, month: number) => {
        const lastDay = getLastDayOfMonth(year, month);
        const { data} = await supabase
            .from('Kalender')
            .select('*')
            .gte('Date', `${year}-${month}-01`)
            .lte('Date', `${year}-${month}-${lastDay}`);

        setKalender(data || []);
        kalenderforhighliteddays = data || [];
    };

    const handleMonthChange = async (date: Dayjs) => {
        if (requestAbortController.current) {
            requestAbortController.current.abort();
        }
        currentMonth = date.month() + 1;
        currentYear = date.year();

        try {
            setIsLoading(true);
            setHighlightedDays([]);
            // Fetch calendar entries for the selected month and year
            await loadKalenderByMonth(currentYear, currentMonth).then(r => setHighlightedDays(kalenderforhighliteddays.map((kalenderforhighliteddays) => dayjs(kalenderforhighliteddays.Date).date())));
            // Update highlighted days
        } catch (error) {
            console.error("Error loading calendar entries:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const getLocation = async () => {
        await navigator.geolocation.getCurrentPosition(success, error, options);
    }

    const options = {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
    };

    function error(err: { code: any; message: any; }) {
        console.warn(`ERROR(${err.code}): ${err.message}`);
    }

    async function success(pos: { coords: any; }) {
        const crd = pos.coords;
        latitude = crd.latitude;
        longitude = crd.longitude;
            const response = await fetch(
                `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current_weather=true&timezone=Europe%2FBerlin`
            );
            const data = await response.json();
            setWeatherData({
                temperature: data.current_weather.temperature,
                weathercode: data.current_weather.weathercode,
            });
    }

    useEffect(() => {
        loadPinnedNotes();
        loadPinnedTodos();
        loadPinnedShoppingLists();
        getLocation();
    }, []);

    useEffect(() => {
        const fetchInitialEntries = async () => {
            setIsLoading(true);
            await loadKalenderByMonth(currentYear, currentMonth).then(r => setHighlightedDays(kalenderforhighliteddays.map((kalenderforhighliteddays) => dayjs(kalenderforhighliteddays.Date).date())));
            setIsLoading(false);
        };
        fetchInitialEntries();
    }, []);

    const handleDayClick = (event: React.MouseEvent<HTMLButtonElement>, day: Dayjs) => {
        setSelectedDate(day);
        setIsDialogOpen(true);
        console.log(kalender);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
    };

    const handleCloseNotizDialog = () => {
        setIsNotizDialogOpen(false);
    };

    const handleToggleItemState = (index: number) => {
        if (selectedShoppingListItem) {
            const newState = [...selectedShoppingListItem.State];
            newState[index] = !newState[index];
            setSelectedShoppingListItem({ ...selectedShoppingListItem, State: newState });
            setIsShoppingListChanged(true);
        }
    };

    const handleToggleTodoState = (index: number) => {
        if (selectedTodoItem) {
            const newState = [...selectedTodoItem.State];
            newState[index] = !newState[index];
            setSelectedTodoItem({ ...selectedTodoItem, State: newState });
            setIsTodoChanged(true);
        }
    };

    const handleCloseViewDialog = async () => {
        if (selectedShoppingListItem && isShoppingListChanged) {
            const { error } = await supabase
                .from('Shoppinglist')
                .update({ State: selectedShoppingListItem.State })
                .eq('id', selectedShoppingListItem.id);

            if (error) {
                console.error('Error updating item state:', error);
            }
            await loadPinnedShoppingLists();
            console.log("Pinned Shopping Lists wurden neu geladen");
        }
        setIsShoppingListDialogOpen(false);
        setSelectedShoppingListItem(null);
        setIsShoppingListChanged(false);
    };

    const handleCloseTodoViewDialog = async () => {
        if (selectedTodoItem && isTodoChanged) {
            const { error } = await supabase
                .from('Todo')
                .update({ State: selectedTodoItem.State })
                .eq('id', selectedTodoItem.id);

            if (error) {
                console.error('Error updating todo state:', error);
            }
            await loadPinnedTodos();
            console.log("Pinned Todos wurden neu geladen");
        }
        setIsTodoDialogOpen(false);
        setSelectedTodoItem(null);
        setIsTodoChanged(false);
    };


    return (
        <div>
            <Grid2 container spacing={2} style={{ display: 'flex', flexDirection: 'column', width: 'auto', height: 'auto' }}>
                <Grid2 container spacing={2} style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', width: 'auto', height: 'auto' }}>
                    {weatherData && (
                        <Grid2 style={{ width: '300px', height: 'auto', marginTop: '40px' }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6">Current Weather</Typography>
                                    <Typography variant="body1">Temperature: {weatherData.temperature}°C</Typography>
                                    <Typography variant="body1">Description: {getWeatherDescription(weatherData.weathercode)}</Typography>
                                </CardContent>
                            </Card>
                        </Grid2>
                    )}
                    <Grid2 style={{ width: '30%', height: 'auto',marginLeft: '10px', marginTop: '10px' }}>
                        <img src='/Pictures/Dashboard.png' alt="Dashboard" style={{ width: '100%', height: 'auto' }} />
                    </Grid2>
                    <Grid2 style={{ width: 'auto', height: 'auto', marginLeft: '10px', marginTop: '10px' }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateCalendar
                                defaultValue={initialValue}
                                views={['year', 'month', 'day']}
                                loading={isLoading}
                                onMonthChange={handleMonthChange}
                                renderLoading={() => <DayCalendarSkeleton />}
                                slots={{
                                    day: (props) => <ServerDay {...props} onClick={handleDayClick} />,
                                }}
                                slotProps={{
                                    day: {
                                        highlightedDays,
                                    } as any,
                                }}
                            />
                        </LocalizationProvider>
                    </Grid2>
                </Grid2>
                <Divider />
                <Grid2 container spacing={2} style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', width: 'auto', height: 'auto', marginTop: '20px' }}>
                    <Grid2 style={{ width: 'auto', height: 'auto' }}>
                        <List sx={{ width: 'auto', bgcolor: 'background.paper', padding: '0 5px' }}>
                            <Typography sx={{paddingTop:'7px'}} variant="h6" align="center">Pinned Notes</Typography>
                            {pinnedNotes.length === 0 ? (
                                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                    <Typography variant="body1">No note pinned</Typography>
                                </Box>
                            ) : (
                                pinnedNotes.map((note, index) => (
                                    <React.Fragment key={note.id}>
                                        <ListItem>
                                            <ListItemButton
                                                key={note.id}
                                                onClick={(event) => handleListItemClick(event, note)}
                                            >
                                                <ListItemIcon>
                                                    <NotesIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={`${note.Titel}`}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                        {index < pinnedNotes.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))
                            )}
                        </List>
                    </Grid2>
                    <Grid2 style={{ width: 'auto', height: 'auto', marginLeft: '10px' }}>
                        <List sx={{ width: 'auto', bgcolor: 'background.paper', padding: '0 5px' }}>
                            <Typography sx={{paddingTop:'7px'}} variant="h6" align="center">Pinned Todos</Typography>
                            {pinnedTodos.length === 0 ? (
                                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                    <Typography variant="body1">No todo pinned</Typography>
                                </Box>
                            ) : (
                                pinnedTodos.map((todo, index) => (
                                    <React.Fragment key={todo.id}>
                                        <ListItem>
                                            <ListItemButton
                                                key={todo.id}
                                                onClick={(event) => handleListItemClick(event, todo)}
                                            >
                                                <ListItemIcon>
                                                    <NotesIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={`${todo.Titel.length > 20 ? todo.Titel.substring(0, 20) + '...' : todo.Titel} - ${todo.Description.length > 30 ? todo.Description.substring(0, 30) + '...' : todo.Description}`}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                        {index < pinnedTodos.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))
                            )}
                        </List>
                    </Grid2>
                    <Grid2 style={{ width: 'auto', height: 'auto', marginLeft: '10px' }}>
                        <List sx={{ width: 'auto', bgcolor: 'background.paper', padding: '0 5px' }}>
                            <Typography sx={{paddingTop:'7px'}} variant="h6" align="center">Pinned Shopping Lists</Typography>
                            {pinnedShoppingLists.length === 0 ? (
                                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                    <Typography variant="body1">No shopping list pinned</Typography>
                                </Box>
                            ) : (
                                pinnedShoppingLists.map((list, index) => (
                                    <React.Fragment key={list.id}>
                                        <ListItem>
                                            <ListItemButton
                                                key={list.id}
                                                onClick={(event) => handleListItemClick(event, list)}
                                            >
                                                <ListItemIcon>
                                                    <NotesIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={`${list.Title} - ${list.Store}`}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                        {index < pinnedShoppingLists.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))
                            )}
                        </List>
                    </Grid2>
                </Grid2>
            </Grid2>
            <Dialog open={isDialogOpen} onClose={handleCloseDialog}>
                <DialogTitle>Your Dates</DialogTitle>
                <DialogContent>
                    {kalender.filter((kalender) => dayjs(kalender.Date).date() === selectedDate?.date()).length === 0 ? (
                        <Typography variant="body1">You have no Dates today</Typography>
                    ) : (
                        <List>
                            {kalender
                                .filter((kalender) => dayjs(kalender.Date).date() === selectedDate?.date())
                                .map((kalender) => (
                                    <ListItem key={kalender.id}>
                                        <ListItemIcon>
                                            {getCategoryIcon(kalender.Category)}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={kalender.Titel}
                                            secondary={kalender.Description}
                                        />
                                    </ListItem>
                                ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Close</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={isNotizDialogOpen} onClose={handleCloseNotizDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{selectedNotiz?.Titel}</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        {selectedNotiz?.Description}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Creation date: {selectedNotiz ? new Date(selectedNotiz.created_at).toLocaleString() : ''}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Last change: {selectedNotiz ? new Date(selectedNotiz.change_date).toLocaleString() : ''}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseNotizDialog} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={isTodoDialogOpen} onClose={handleCloseTodoViewDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{selectedTodoItem?.Titel}</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        {selectedTodoItem?.Description}
                    </Typography>
                    <List sx={{ maxHeight: 200, overflow: 'auto', marginTop: 2, bgcolor: 'rgb(243, 229, 229)', borderRadius: 2 }}>
                        {selectedTodoItem?.Tasks.map((item, index) => (
                            <React.Fragment key={index}>
                                <ListItem>
                                    <Checkbox
                                        edge="start"
                                        checked={selectedTodoItem.State[index]}
                                        onChange={() => handleToggleTodoState(index)}
                                        tabIndex={-1}
                                        disableRipple
                                    />
                                    <ListItemText primary={`${item}`} />
                                </ListItem>
                                {index < selectedTodoItem.Tasks.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseTodoViewDialog} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isShoppingListDialogOpen} onClose={() => setIsShoppingListDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{selectedShoppingListItem?.Title}</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        {selectedShoppingListItem?.Description}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Store: {selectedShoppingListItem?.Store}
                    </Typography>
                    <List sx={{ maxHeight: 200, overflow: 'auto', marginTop: 2, bgcolor: 'rgb(243, 229, 229)', borderRadius: 2 }}>
                        {selectedShoppingListItem?.Parts.map((part, index) => (
                            <React.Fragment key={index}>
                                <ListItem>
                                    <ListItemText primary={`${selectedShoppingListItem.Amount[index]}x ${part}`} />
                                    <Checkbox
                                        edge="end"
                                        checked={selectedShoppingListItem.State[index]}
                                        onChange={() => handleToggleItemState(index)}
                                        tabIndex={-1}
                                        disableRipple
                                    />
                                </ListItem>
                                {index < selectedShoppingListItem.Parts.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseViewDialog} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default Dashboard;